import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import React, { useCallback, useMemo, useState } from "react"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { fetchResources, FetchResourcesResponse, ServiceCenterResourcesQuery } from "../resourcesApi"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { OrganizationResourceUnion, ResourceType } from "../resourcesTypes"
import { getEditServiceCenterResourceMobileAvRoute } from "../../../../../../constants/routes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

const transformData = (data: FetchResourcesResponse) => {
    return data.resources
}

export function ServiceCenterAdminResourcesMobileAV() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const mobileAV = useApiQuery(fetchResources, {
        queryName: ServiceCenterResourcesQuery.SERVICE_CENTER_RESOURCES,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            type: ResourceType.MOBILE_AV,
        },
        enabled: Boolean(serviceCenterId),
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(mobileAV)

    const columns = useMemo<MRT_ColumnDef<OrganizationResourceUnion>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("service-center-admin-organization-resources-table-mobile-av-header-id"),
                accessorKey: "reference",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("service-center-admin-organization-resources-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    accessorKey: "status",
                    header: l10n.getString("service-center-admin-organization-resources-table-header-status"),
                },
                (row) => row.status === "Active",
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<OrganizationResourceUnion>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    navigate(getEditServiceCenterResourceMobileAvRoute(row.original.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-organization-resources-table-edit-item">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <DestructiveMenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    setConfirmModalState({
                        showConfirmModal: true,
                        confirmModalText: row.original.name,
                        itemId: row.original.id,
                    })
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-organization-resources-table-delete-item">
                    <span>Delete</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
        ],
        [],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString(
                        "service-center-admin-organization-resources-table-mobile-av-equipment-delete-modal-title",
                    )}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={() => {}} //todo
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                    open={confirmModalState.showConfirmModal}
                />
            )}
        </>
    )
}
